import { faBuilding, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { Customer, CustomerLocation } from '@/services/customer.service';
import type { AddressSelection } from '@/services/equipment-intake.service';

/**
 * The one place that decides how an address is NAMED on screen.
 *
 * The operator never learns whether an address came from the customer record or
 * from a branch row — both are just places with names they recognise. That is a
 * deliberate product rule, so the mapping lives here rather than being re-derived
 * per screen where one copy would inevitably drift into "Customer address".
 */

export interface AddressOption {
  /** Stable dropdown key. 'company' for the customer's own address, else the location id. */
  id: string;
  /** The real business name. Never a category, never a description. */
  name: string;
  /** Which table it came from — used for the icon and the edit path, never shown. */
  kind: 'company' | 'place';
  /** What gets submitted. Selections only; the server resolves and freezes. */
  selection: AddressSelection;
}

export const COMPANY_OPTION_ID = 'company';

/**
 * The customer's own registered address first, then its branches.
 *
 * The company entry is not decoration: most intakes bill to it, and a customer
 * with no branches would otherwise have nothing to pick at all.
 */
export function buildAddressOptions(
  customer: Customer | null,
  locations: CustomerLocation[],
): AddressOption[] {
  if (!customer) return [];
  return [
    {
      id: COMPANY_OPTION_ID,
      name: customer.name,
      kind: 'company',
      selection: { source: 'CUSTOMER_DEFAULT' },
    },
    ...locations.map((l) => ({
      id: l.id,
      name: l.name,
      kind: 'place' as const,
      selection: { source: 'LOCATION' as const, locationId: l.id },
    })),
  ];
}

/** Which option a stored selection corresponds to. */
export function optionIdFor(selection: AddressSelection): string {
  return selection.source === 'CUSTOMER_DEFAULT'
    ? COMPANY_OPTION_ID
    : (selection.locationId ?? COMPANY_OPTION_ID);
}

export function iconFor(option: Pick<AddressOption, 'kind'>): IconDefinition {
  return option.kind === 'company' ? faBuilding : faLocationDot;
}

/** Case-insensitive match over the name and the town, so "chennai" finds it. */
export function matchesQuery(option: AddressOption, location: CustomerLocation | null, q: string) {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    option.name.toLowerCase().includes(needle) ||
    (location?.city ?? '').toLowerCase().includes(needle) ||
    (location?.addressLine1 ?? location?.address ?? '').toLowerCase().includes(needle)
  );
}
